import React from "react";
import styled from "styled-components";
import { useSelector } from "react-redux";
import { RootState } from "../../types/RootState";
import PartnerIndicationIcon from "../../images/PARTNER INDICATION.svg";

const IndicatorBadge = styled.span`
  font-size: 0.625rem;
  font-weight: 600;
  margin-left: 6px;
  vertical-align: middle;
  min-width: 16px;
  text-align: center;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const IconWrapper = styled.img`
  width: 20px;
  height: 20px;
`;

interface PartnerIndicatorProps {
  /** Organization type of the entity owner */
  organisationType?: "SYSTEM" | "PARTNER" | "NORMAL";
  /** Current user's organization type for comparison */
  currentUserOrgType?: "SYSTEM" | "PARTNER" | "NORMAL";
  /** Size of the indicator */
  size?: "sm" | "md";
  /** Custom className */
  className?: string;
  /** Show indicator for System level entities */
  showSystem?: boolean;
  /** Show indicator for Partner level entities */
  showPartner?: boolean;
  /** Custom tooltip title (overrides default) */
  title?: string;
}

/**
 * PartnerIndicator - A standardized component to show organizational level of entities
 *
 * Usage scenarios:
 * - Show when geozone tags are from partner level
 * - Indicate system-level configurations
 * - Display entity ownership hierarchy
 *
 * @param organisationType - The organization type of the entity owner
 * @param currentUserOrgType - Current user's organization type for context
 * @param size - Size of the indicator (sm or md)
 * @param showSystem - Whether to show indicator for SYSTEM entities (default: true)
 * @param showPartner - Whether to show indicator for PARTNER entities (default: true)
 */
const PartnerIndicator: React.FC<PartnerIndicatorProps> = ({
  organisationType,
  currentUserOrgType,
  size = "sm",
  className = "",
  showSystem = true,
  showPartner = true,
}) => {
  const language = useSelector((state: RootState) => state.language);

  // Don't show indicator if the entity is owned by the same org type as current user
  if (organisationType === currentUserOrgType) {
    return null;
  }

  // Don't show for NORMAL organizations (they're the default)
  if (organisationType === "NORMAL") {
    return null;
  }

  const getIndicatorProps = () => {
    switch (organisationType) {
      case "SYSTEM":
        if (!showSystem) return null;
        return {
          content: "S",
          title: language.system_level_entity_key,
          className: "border-secondary",
        };
      case "PARTNER":
        if (!showPartner) return null;
        return {
          content: <IconWrapper src={PartnerIndicationIcon} alt="Partner" />,
          title: language.partner_level_entity_key,
          className: "border-secondary",
        };
      default:
        return null;
    }
  };

  const indicatorProps = getIndicatorProps();

  if (!indicatorProps) {
    return null;
  }

  return (
    <IndicatorBadge
      title={indicatorProps.title}
      className={`${indicatorProps.className} ${className}`}
      style={{ fontSize: size === "sm" ? "0.625rem" : "0.75rem" }}
    >
      {indicatorProps.content}
    </IndicatorBadge>
  );
};

export default PartnerIndicator;
